import type { DashboardPromoBannerConfig } from 'src/configs/dashboards/energy-dashboard-promo-banners';
import {
	getEnergyDashboardPromoBanners,
	resolveVisibleDashPromoBanners,
} from 'src/configs/dashboards/energy-dashboard-promo-banners';
import { getCustomDashboardPromoBanners } from 'src/configs/dashboards/custom-dashboard-promo-banners';
import { ENERGY_DASHBOARD_ID } from 'src/components/EnergyMonitoring/v2/energy-v2-types';
import { isProtectedDashboard } from 'src/components/Dashboards/dashboard-kind';
import {
	isCustomDashboardPromoSlotEnabled,
	isDashPromoEmptyStateEnabled,
	isEnergyDashboardPromoSlotEnabled,
} from 'src/stores/features/dev/dashboard-promo-slots';

export const DASH_PROMO_WIDGET_TYPE = 'dashPromo' as const;
export const DASH_PROMO_WIDGET_ID_PREFIX = 'dash-promo-';

export type DashPromoSurface = 'energy' | 'custom';

export const isDashPromoWidget = (widget: {
	type?: string;
	id?: string;
	isVirtual?: boolean;
}): boolean =>
	widget?.type === DASH_PROMO_WIDGET_TYPE ||
	widget?.isVirtual === true ||
	String(widget?.id ?? '').startsWith(DASH_PROMO_WIDGET_ID_PREFIX);

export const resolveDashPromoSurface = (dashboardId: string | number): DashPromoSurface | null => {
	if (Number(dashboardId) === ENERGY_DASHBOARD_ID) return 'energy';
	if (!isProtectedDashboard(dashboardId)) return 'custom';
	return null;
};

export const getDashPromoBannersForSurface = (
	surface: DashPromoSurface,
): DashboardPromoBannerConfig[] =>
	surface === 'energy' ? getEnergyDashboardPromoBanners() : getCustomDashboardPromoBanners();

export const isDashPromoSlotEnabledForSurface = (surface: DashPromoSurface): boolean =>
	surface === 'energy' ? isEnergyDashboardPromoSlotEnabled() : isCustomDashboardPromoSlotEnabled();

/**
 * Builds a virtual (non-persisted) promo widget for the left column.
 * Sample/generic banners require the Dev slot toggle (default off).
 * Verivox still renders for de_users when the slot is off.
 */
export const buildVirtualDashPromoWidgets = (
	dashboardId: string | number,
): Array<{
	id: string;
	type: typeof DASH_PROMO_WIDGET_TYPE;
	col: 'left';
	position: number;
	title: string;
	showWidgetTitle: boolean;
	isVirtual: true;
	promoBanners: DashboardPromoBannerConfig[];
	cards: Array<{ id: string; _type: 'promo'; position: number }>;
}> => {
	const surface = resolveDashPromoSurface(dashboardId);
	if (!surface) {
		return [];
	}

	const slotOn = isDashPromoSlotEnabledForSurface(surface);
	const dismissSurface = surface === 'energy' ? 'energyDashboard' : 'customDashboard';
	const banners = resolveVisibleDashPromoBanners(dismissSurface, 'widget', slotOn);
	if (banners.length === 0 && !(slotOn && isDashPromoEmptyStateEnabled())) {
		return [];
	}

	return [
		{
			id: `${DASH_PROMO_WIDGET_ID_PREFIX}${surface}`,
			type: DASH_PROMO_WIDGET_TYPE,
			col: 'left',
			position: -1,
			title: 'Promotions',
			showWidgetTitle: true,
			isVirtual: true,
			promoBanners: banners,
			cards: [
				{
					id: `${DASH_PROMO_WIDGET_ID_PREFIX}${surface}-card`,
					_type: 'promo',
					position: 0,
				},
			],
		},
	];
};
